/**
 * Analytics types following the analytics team spec for auth-app.
 *
 * Event naming convention:
 * - Connection-View: Page/screen viewed
 * - Connection-Click: User clicked a CTA
 * - Connection-Status: Action result (success/failure)
 */

import { EntryPoint, IdentificationMethod } from 'session/types';

export type EventName = 'Connection-View' | 'Connection-Click' | 'Connection-Status';

export type PageName =
  | 'sign-in-to-melio'
  | 'forgot-password'
  | 'check-email'
  | 'set-new-password'
  | 'create-your-account'
  | 'enter-your-verification-code'
  | 'verify-it-is-you'
  | 'lets-secure-your-account'
  | 'enter-6-digits-code'
  | 'domain-transition-banner';

export type Flow = 'sign-in' | 'sign-up';

export type Cta =
  | 'continue'
  | 'sign-up'
  | 'forgot-password'
  | 'sign-in-with-google'
  | 'sign-in-with-xero'
  | 'sign-in-with-intuit'
  | 'send-reset-link'
  | 'go-back'
  | 'save-new-password'
  | 'sign-in'
  | 'send-verification-code'
  | 'not-now'
  | 'change'
  | 'see-how'
  | 'dismiss';

export type StatusType = 'success' | 'failure';

export type ErrorType =
  | 'login-info-incorrect'
  | 'invalid-email'
  | 'invalid-password'
  | 'empty-email'
  | 'empty-password'
  | 'too-many-failed-attempts'
  | 'invalid-phone-number'
  | 'invalid-code'
  | 'too-many-resend'
  | 'password-not-confirmed';

/**
 * Base properties required for all events
 */
export interface BaseEventProperties {
  PageName: PageName;
  Flow: Flow;
  Intent: string;
  Variant?: string | null;
  EntryPoint: EntryPoint;
}

/**
 * Properties for Connection-View events
 */
export interface ViewEventProperties extends BaseEventProperties {
  IdentificationMethod?: IdentificationMethod;
}

/**
 * Properties for Connection-Click events
 */
export interface ClickEventProperties extends BaseEventProperties {
  Cta: Cta;
  IdentificationMethod?: IdentificationMethod;
  EmailInteraction?: boolean;
  PasswordInteraction?: boolean;
  PasswordManualEntry?: boolean;
  NewPasswordInteraction?: boolean;
  ConfirmPasswordInteraction?: boolean;
  CodeInteraction?: boolean;
  PhoneInteraction?: boolean;
}

/**
 * Properties for Connection-Status events
 */
export interface StatusEventProperties extends BaseEventProperties {
  StatusType: StatusType;
  Cta?: Cta;
  IdentificationMethod?: IdentificationMethod;
  ErrorType?: ErrorType;
  CodeInteraction?: boolean;
}

/**
 * Marketing events for successful sign-up flows (used by marketing analytics).
 * No payload — event name only.
 */
export type MarketingRegisterSuccessEventName =
  | 'marketing_register_sign-up-with-email-password-success'
  | 'marketing_register_sign-up-with-intuit-success'
  | 'marketing_register_sign-up-with-xero-success'
  | 'marketing_register_sign-up-with-google-success';

/**
 * Discriminated union for track events.
 * TypeScript enforces correct properties based on event name.
 */
export type TrackEvent =
  | { eventName: 'Connection-View'; properties: ViewEventProperties }
  | { eventName: 'Connection-Click'; properties: ClickEventProperties }
  | { eventName: 'Connection-Status'; properties: StatusEventProperties }
  | { eventName: MarketingRegisterSuccessEventName };

/**
 * User identification params for Segment.
 */
export interface IdentifyParams {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  orgId?: number;
}

/**
 * Analytics configuration
 */
export interface AnalyticsConfig {
  enabled: boolean;
  printEvents: boolean;
  segmentKey: string;
}

/**
 * Segment Analytics interface (subset of what we need)
 */
export interface SegmentAnalytics {
  track: (event: string, properties?: Record<string, unknown>, callback?: () => void) => void;
  identify: (userId?: string, traits?: Record<string, unknown>, callback?: () => void) => void;
  page: (name?: string, properties?: Record<string, unknown>) => void;
  ready: (callback: () => void) => void;
  reset: () => void;
}

/**
 * Segment destinations that must be blocked from receiving events when the
 * user has opted out of sale under CCPA (via GPC or a recorded DB opt-out).
 * Matches platform-app's `excludedSalesIntegrations` so gating is consistent
 * across Melio's web apps.
 */
export const excludedSalesIntegrations = [
  'Facebook Pixel',
  'Google Tag Manager',
  'AdWords',
  'Google Ads (Classic)',
  'Twitter Ads',
  'Google AdWords New',
  'Google Analytics',
];

type NavigatorWithGlobalPrivacyControl = Navigator & { globalPrivacyControl?: boolean };

/**
 * True when the browser sends the Global Privacy Control signal, which under
 * CCPA is treated the same as a recorded "Do Not Sell" opt-out.
 */
export const isGpcOptedOutOfSale = (): boolean => Boolean((navigator as NavigatorWithGlobalPrivacyControl).globalPrivacyControl);

/**
 * Builds the Segment `integrations` option that blocks the CCPA-excluded
 * sale/ad destinations for a single track/identify/page call, mirroring
 * platform-app's `getSegmentOptions`. Returns `undefined` when the user has
 * not opted out, so callers don't send an empty `integrations` payload.
 */
export const getSalesIntegrationsOptions = (optedOutOfSale: boolean): Record<string, boolean> | undefined => {
  if (!optedOutOfSale) {
    return undefined;
  }

  return excludedSalesIntegrations.reduce((acc, integration) => ({ ...acc, [integration]: false }), {} as Record<string, boolean>);
};
